import { ChevronDownIcon, MOBILE_SCREEN_SIZE } from "@brandclub/common-ui";
import { Box } from "@mui/material";
import { useSelector } from "@xstate/react";
import { AnimatePresence, motion } from "framer-motion";
import _startCase from "lodash/startCase";

import CardBrandIcon from "../../../../pages/AppPages/Dashboard/Wallet/CardBrandIcon";
import { StoreAnimatedHeightDiv } from "../../../../StoreComponents/StoreAnimatedHeightDiv";
import { useCheckoutContext } from "../../../CheckoutMachine";

import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";

import Stripe from "stripe";
import {
  AsyncCheckoutOrderType,
  UserRetailerCheckoutOrder,
} from "../../../types";
import { PaymentMethodDisplay } from "../../Checkout/CheckoutForm/components/PaymentMethodDisplay";
import InfoBox from "../InfoBox";

import { PaymentSelectionForm } from "./PaymentSelectionForm";
import { useEffect, useState } from "react";
import { getPaymentMethodsAndIntent } from "@/api/rest/checkoutApi";

const PaymentMethod = ({
  paymentMethod,
  stripePaymentMethod,
  orderType,
}: {
  paymentMethod: UserRetailerCheckoutOrder["paymentMethod"];
  stripePaymentMethod: Stripe.PaymentMethod | undefined;
  onAddPaymentMethod: () => void;
  orderType?: AsyncCheckoutOrderType;
}) => {
  const isGuest = orderType === AsyncCheckoutOrderType.GUEST;

  if (!paymentMethod) {
    if (stripePaymentMethod) {
      return <PaymentMethodDisplay paymentMethod={stripePaymentMethod} />;
    }
    return (
      <InfoBox message="Please add a payment method to your retailer account." />
    );
  }

  if (isGuest && stripePaymentMethod) {
    return <PaymentMethodDisplay paymentMethod={stripePaymentMethod} />;
  }

  const cardBrand = paymentMethod?.cardIssuer?.toLocaleLowerCase();
  const cardNumberEnding = paymentMethod?.cardNumberEnding;

  return (
    <div className="info_box">
      <div className="info_row">
        <CardBrandIcon className="icon" alt="icon" cardBrand={cardBrand} />
        {_startCase(paymentMethod?.cardIssuer || "")} ending in{" "}
        {cardNumberEnding}
      </div>
    </div>
  );
};
const PaymentSection = () => {
  const { actorRef, send } = useCheckoutContext();

  const open = useSelector(
    actorRef,
    (state) => state.context.sectionState.payment.open
  );

  const errorMessage = useSelector(
    actorRef,
    (state) => state.context.sectionState.payment.errorMessage
  );

  const userRetailerCheckoutOrder = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
  );

  const toggleOpen = () => {
    if (open) {
      send({ type: "ON_SESSION_CLOSE", data: { sessionToClose: "payment" } });
    } else {
      send({ type: "ON_SESSION_OPEN", data: { sessionToOpen: "payment" } });
    }
  };

  // Prefetch Stripe payment methods and intent once per order
  const [prefetchedMethods, setPrefetchedMethods] =
    useState<any[] | undefined>(undefined);
  const [prefetchedClientSecret, setPrefetchedClientSecret] =
    useState<string | undefined>(undefined);
  useEffect(() => {
    const orderId = userRetailerCheckoutOrder?.orderId;
    if (!orderId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await getPaymentMethodsAndIntent({ orderId });
        if (cancelled) return;
        setPrefetchedMethods(res?.paymentMethods || []);
        setPrefetchedClientSecret(
          res?.stripePaymentIntent?.client_secret || undefined
        );
      } catch {
        // ignore
      }
    };
    // Only fetch if not already fetched for this order
    if (!prefetchedMethods && !prefetchedClientSecret) {
      run();
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRetailerCheckoutOrder?.orderId]);

  return (
    <Box className="section">
      <div
        className="section_head"
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="title">{"Payment method"}</div>
        {errorMessage ? (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "33px",
              height: "33px",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
            onClick={toggleOpen}
          ></ChevronDownIcon>
        ) : null}
      </div>
      <StoreAnimatedHeightDiv>
        <StoreErrorV2
          errorMessage={errorMessage}
          sx={{
            width: "fit-content",
            marginTop: "2.2px",
            marginBottom: "24px",
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              marginTop: "0px",
              marginBottom: "15px",
            },
          }}
        />

        <AnimatePresence mode="wait">
          <motion.div
            layout="position"
            key={open ? "edit" : "view"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            {open ? (
              <PaymentSelectionForm
                methods={prefetchedMethods}
                clientSecret={prefetchedClientSecret}
              />
            ) : (
              <PaymentMethod
                paymentMethod={userRetailerCheckoutOrder?.paymentMethod}
                stripePaymentMethod={
                  userRetailerCheckoutOrder?.userCheckoutInfo
                    ?.stripePaymentMethod
                }
                orderType={userRetailerCheckoutOrder?.orderType}
                onAddPaymentMethod={() => {
                  send({
                    type: "ON_SESSION_OPEN",
                    data: { sessionToOpen: "payment" },
                  });
                }}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </StoreAnimatedHeightDiv>
    </Box>
  );
};

export default PaymentSection;
